import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  SectionIntro,
  FeatureGrid,
  StatBand,
  Quote,
  LogoCloud,
  CTASection,
  Reveal,
} from './kit.jsx'

const STATS = [
  { value: '2.1x', label: 'Average reply rate vs. blast tools' },
  { value: '38%', label: 'More booked jobs in the first quarter' },
  { value: '10,942', label: 'Verified leads worked across teams' },
  { value: '50', label: 'U.S. regions covered on day one' },
]

const QUOTES = [
  {
    quote:
      'We killed three list subscriptions the week we switched. Every account in our territory was already loaded, and my team just works their region instead of arguing over spreadsheets.',
    name: 'Marcus Bell',
    role: 'Owner, Bell Analytics — Columbus, OH',
  },
  {
    quote:
      'Sending sequences from our own Gmail changed everything. Replies land in the same inbox we already live in, and our reply rate more than doubled versus the blast tool we dumped.',
    name: 'Dana Ruiz',
    role: 'Sales Lead, Summit Health Systems — Denver, CO',
  },
  {
    quote:
      'The pipeline view finally told us which regions are worth the drive. We cut two markets that were bleeding us and doubled down on the one closing deals. That call alone paid for the year.',
    name: 'Tariq Nolan',
    role: 'VP Sales, Ridgeline Exteriors — Austin, TX',
  },
  {
    quote:
      'I used to spend Sunday nights building lead lists. Now I pick an industry and a region and the accounts are just there — real contacts, service area, annual volume. My reps start Monday already dialed in.',
    name: 'Priya Chandra',
    role: 'Director of Growth, Cornerstone Builders — Phoenix, AZ',
  },
  {
    quote:
      'We booked 38% more demos in our first quarter without adding a single rep. The follow-up steps just kept running while my team was heads-down on calls all day.',
    name: 'Wes Okafor',
    role: 'Founder, Apex Commercial Software — Atlanta, GA',
  },
  {
    quote:
      'Onboarding took an afternoon, not a quarter. I asked the built-in assistant which lead types were converting and had my answer before the coffee was cold. No analyst, no export.',
    name: 'Hannah Vogel',
    role: 'Operations Manager, Northline Contracting — Minneapolis, MN',
  },
]

const WHY_SWITCH = [
  {
    icon: 'boxes',
    title: 'One source of truth',
    body: 'Teams retire the patchwork of purchased lists, shared inboxes, and spreadsheets. Everyone works the same 10,942 verified companies, scoped to their territory.',
  },
  {
    icon: 'mail',
    title: 'Outreach that lands',
    body: 'Sequences send from each rep’s own Gmail, so messages arrive from a real person and replies come straight back — the reason reply rates jump 2.1x on average.',
  },
  {
    icon: 'chart',
    title: 'Revenue you can see',
    body: 'Deal Flow and the Data view roll up every region, lead type, and campaign, so owners finally know which markets are producing jobs and which to cut.',
  },
  {
    icon: 'rocket',
    title: 'Live in an afternoon',
    body: 'No data project and no long implementation. Reps sign in and their market is already loaded, filtered, and ready to sequence the same day.',
  },
  {
    icon: 'users',
    title: 'The whole crew, aligned',
    body: 'Assign regions, invite reps, and keep collaboration in one workspace — no more duplicate outreach or leads slipping between people.',
  },
  {
    icon: 'sparkle',
    title: 'Answers on demand',
    body: 'Ask the in-app assistant which regions or campaigns are working and get an answer straight from live workspace data — no analyst in the loop.',
  },
]

export default function CustomersPage() {
  return (
    <MarketingShell active="">
      <Seo
        title="Customers"
        description="See how B2B sales teams use Revailo Sales to lift reply rates 2.1x, book 38% more demos, and work 10,942 verified leads across every region."
        path="/customers"
        keywords="Revailo Sales customers, b2b sales testimonials, case studies, sales results"
      />

      <PageHero
        eyebrow="Customers"
        title={
          <>
            Teams that stopped sourcing <br className="max-[640px]:hidden" />
            and started closing.
          </>
        }
        lead="B2B sales teams run their entire sales motion on Revailo Sales — from the first verified lead to the signed contract. Here is what that looks like in the field."
        primary={{ label: 'Start free', to: '/signup' }}
        secondary={{ label: 'Book a demo', to: '/demo' }}
      />

      <Section sm>
        <Container>
          <Reveal>
            <StatBand stats={STATS} />
          </Reveal>
        </Container>
      </Section>

      <Section id="stories">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="In their words"
              title={<>Owners and sales leads, on the record.</>}
              lead="Real trades, real regions. These are the operators running B2B teams on Revailo Sales every day."
            />
          </Reveal>
          <div className="mt-14">
            <div className="grid grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-2 min-[960px]:grid-cols-3">
              {QUOTES.map((q, i) => (
                <Reveal key={q.name} delay={(i % 3) * 80}>
                  <Quote quote={q.quote} name={q.name} role={q.role} />
                </Reveal>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      <Section sm>
        <Container>
          <Reveal>
            <LogoCloud
              label="Trusted by B2B sales teams across the country"
              logos={['Bell Analytics', 'Summit Health', 'Ridgeline Logistics', 'Cornerstone Software', 'Apex Commercial', 'Northline']}
            />
          </Reveal>
        </Container>
      </Section>

      <Section id="why">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Why teams switch"
              title="The same outcomes, every time"
              lead="Different markets, one pattern: fewer tools, higher reply rates, and a pipeline the owner can actually read."
            />
          </Reveal>
          <div className="mt-14">
            <Reveal>
              <FeatureGrid items={WHY_SWITCH} cols={3} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <CTASection
        eyebrow="Join them"
        title="Your market is already in the database."
        body="Start free and see every qualified company in your region today. No credit card, no data project."
        primary={{ label: 'Start free', to: '/signup' }}
        secondary={{ label: 'Talk to sales', to: '/contact' }}
      />
    </MarketingShell>
  )
}
